<script setup lang="ts">
type MultipleTypeValid = string | number | boolean | object | any[] | undefined

const props = withDefaults(
	defineProps<{
		modelValue?: MultipleTypeValid
		value?: MultipleTypeValid
		list: { label: string }[]
		width?: string
		height?: string
		sizeIcon?: string
		fontSize?: string
		disabled?: boolean
		placeholder?: string
		bgList?: string
		isLottery?: boolean
		showIconLeading?: boolean
		translate?: boolean
	}>(),
	{
		modelValue: undefined,
		value: undefined,
		width: 'w-full max-w-xl',
		height: 'h-14 smm:h-16',
		sizeIcon: 'w-5 h-5',
		fontSize: 'text-base smm:text-lg',
		disabled: false,
		placeholder: 'Where are you from?',
		bgList: 'bg-white bg-opacity-5',
		isLottery: false,
		showIconLeading: true,
		translate: false,
	},
)

const { t: $t } = useI18n()
const emit = defineEmits(['update:modelValue', 'change'])

const isMobile = ref(false)

const proxyChecked = computed<MultipleTypeValid>({
	get() {
		return (props.modelValue || props.value) ?? undefined
	},
	set(val) {
		emit('update:modelValue', val)
	},
})

const updateSelect = (value?: object) => {
	emit('change', value)
}

const checkScreenSize = async () => {
	const currentWidth = window.innerWidth
	isMobile.value = currentWidth <= 480
}

const findSearchInput = () =>
	document.querySelector('input[role="combobox"][aria-autocomplete="list"]') as HTMLInputElement | null

const onOpen = async () => {
	if (!isMobile.value) return
	// Espera a que el menú monte y HeadlessUI haga su autofocus
	await nextTick()
	requestAnimationFrame(() => {
		const input = findSearchInput()

		if (!input) return

		// Evita teclado al abrir
		input.readOnly = true
		input.inputMode = 'none'
		input.blur()

		// Al tocar el buscador, habilita teclado y enfoca
		const enable = () => {
			input.readOnly = false
			input.inputMode = 'text'
			input.focus()
			input.removeEventListener('click', enable)
		}
		input.addEventListener('click', enable)
	})
}

onMounted(() => {
	checkScreenSize()
})
</script>

<template>
	<USelectMenu
		v-model="proxyChecked"
		clear-search-on-close
		:placeholder="$t(placeholder)"
		:searchable-placeholder="$t('Search...')"
		searchable
		:options="list"
		:disabled="props.disabled"
		color="white"
		:variant="`${proxyChecked ? 'outline' : 'none'}`"
		size="xl"
		option-attribute="label"
		by="id"
		:ui="{
			rounded: 'rounded-full',
			icon: {
				leading: {
					pointer: 'pointer-events-auto',
				},
			},
		}"
		:ui-menu="{
			background: `${props.bgList} space-y-1`,
			padding: 'p-2',
			icon: {
				active: 'text-white',
			},
			option: {
				container: 'w-full',
				size: 'text-base',
				active: 'bg-white bg-opacity-40',
				color: 'text-neutral-50',
				selected: 'text-neutral-50 bg-white bg-opacity-40 border-none',
				base: 'bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-40',
				selectedIcon: {
					base: 'text-neutral-50',
				},
			},
			select: 'border-primary-500',
			input: 'rounded-lg bg-neutral-100 h-10 font-bold text-base placeholder:text-sm',
		}"
		:select-class="`${props.height} ${props.fontSize} ${!props.showIconLeading ? 'ps-5' : 'ps-14'} text-white bg-neutral-50 bg-opacity-10 font-semibold  select-custom border-primary-500`"
		:class="props.width"
		@change="updateSelect"
		@open="onOpen"
	>
		<template
			v-if="props.isLottery && proxyChecked"
			#label="{ selected }"
		>
			<div class="flex items-center gap-4 py-2.5 w-full">
				<div class="bg-white max-w-28 w-full py-1 px-3 rounded-full">
					<NuxtImg
						:src="`/cdn/${selected.image}`"
						:alt="selected.label"
						:title="selected.label"
						loading="eager"
						layout="fill"
						object-fit="contain"
						sizes="80px"
						class="max-h-6 h-auto w-auto mx-auto"
					/>
				</div>
				<p class="text-base flex-1">{{ selected.label }}</p>
			</div>
		</template>
		<template
			v-if="props.showIconLeading"
			#leading
		>
			<slot name="icon-leading">
				<UIcon
					name="i-material-symbols-light:location-on-outline"
					class="w-7 h-7 pl-5 text-white"
				/>
			</slot>
		</template>
		<template #option="{ option: item, selected }">
			<div
				v-if="props.isLottery"
				class="flex items-center gap-4 py-2.5 w-full"
			>
				<div class="bg-white max-w-28 w-full py-1 px-3 rounded-full">
					<NuxtImg
						:src="`/cdn/${item.image}`"
						:alt="item.label"
						:title="item.label"
						loading="eager"
						layout="fill"
						object-fit="contain"
						sizes="80px"
						class="max-h-6 h-auto w-auto mx-auto"
					/>
				</div>
				<p
					class="truncate flex-1"
					:class="{ 'font-bold': !!proxyChecked ? item.id === proxyChecked.id : false }"
				>
					{{ item.label }}
				</p>
			</div>
			<span
				v-else
				class="truncate"
				:class="{ 'font-bold': selected }"
				>{{ props.translate === true ? $t(item.label) : item.label }}</span
			>
		</template>
		<template #trailing>
			<UIcon
				name="i-ep:arrow-down-bold"
				:class="[props.sizeIcon, props.disabled ? 'text-transparent' : 'text-neutral-50']"
			/>
		</template>
	</USelectMenu>
</template>

<style lang="postcss">
.select-custom {
	.start-0,
	.end-0 {
		@apply px-5;
	}
}
</style>
